import type { PropsWithChildren } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { useId } from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import type { BlockProps } from '../block/block'
import { Block } from '../block/block'
import type { RadioButtonProps } from './RadioButton'
import { RadioButtonContextProvider } from './RadioButtonContextProvider'

export interface RadioButtonGroupProps
  extends Omit<BlockProps, 'onChange'>,
    Pick<RadioButtonProps, 'disabled' | 'value' | 'size' | 'name'> {
  initialValue?: string
  onChange: (value: string | number | undefined) => void
}

export const RadioButtonGroup = ({
  disabled = false,
  onChange,
  value,
  children,
  initialValue,
  size = 'xl',
  name: nameProp,
  ...rest
}: PropsWithChildren<RadioButtonGroupProps>) => {
  const [currentValue, setCurrentValue] = useState<string | number | undefined>(
    initialValue
  )
  const id = useId('radio-group-option')
  const name = useMemo(() => nameProp || id, [nameProp])
  const providerValue = useMemo(() => {
    function updateState(nextValue: string | number | undefined) {
      setCurrentValue(nextValue)
      onChange?.(nextValue)
    }

    return {
      name,
      size,
      updateState,
      disabledAll: disabled,
      inGroup: true,
      value: currentValue
    }
  }, [size, disabled, currentValue, onChange, name])

  useEffect(() => {
    if (value === undefined) return
    setCurrentValue(value)
  }, [value])

  return (
    <RadioButtonContextProvider value={providerValue}>
      <Block role='radiogroup' gap={12} {...rest}>
        {children}
      </Block>
    </RadioButtonContextProvider>
  )
}

if (__DEV__) {
  RadioButtonGroup.displayName = 'RadioButtonGroup'
}
